import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import AdminTable from '../components/AdminTable';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { getAllFAQs, createFAQ, updateFAQ, deleteFAQ } from '../api/apiService';

const AdminFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const response = await getAllFAQs();
      setFaqs(response.data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const columns = [
    { id: 'question', label: 'Question' },
    { id: 'status', label: 'Status' },
  ];

  const handleOpen = (faq = null) => {
    setEditingFAQ(faq || { question: '', answer: '', status: 'Active' });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingFAQ(null);
  };

  const handleSubmit = async () => {
    // Sanitize payload
    const payload = {
      question: editingFAQ.question,
      answer: editingFAQ.answer,
      status: editingFAQ.status || 'Active'
    };

    try {
      if (editingFAQ.id) {
        await updateFAQ(editingFAQ.id, payload);
      } else {
        await createFAQ(payload);
      }
      fetchFAQs();
      handleClose();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      alert(error.response?.data?.message || 'Error saving FAQ. Ensure question and answer are long enough.');
    }
  };

  const handleDelete = (faq) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (faqToDelete) {
      try {
        await deleteFAQ(faqToDelete.id);
        fetchFAQs();
        setDeleteDialogOpen(false);
        setFaqToDelete(null);
      } catch (error) {
        console.error('Error deleting FAQ:', error);
      }
    }
  };

  const filteredFAQs = Array.isArray(faqs) ? faqs.filter((faq) =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  return (
    <Box>
      <AdminTable
        title="FAQs Management"
        columns={columns}
        data={filteredFAQs}
        page={0}
        rowsPerPage={25}
        totalCount={filteredFAQs.length}
        onPageChange={() => {}}
        onRowsPerPageChange={() => {}}
        onSearch={setSearchQuery}
        onAdd={() => handleOpen()}
        onEdit={(faq) => handleOpen(faq)}
        onDelete={handleDelete}
        showPagination={false}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: '16px' } } }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {editingFAQ?.id ? 'Edit FAQ' : 'Add New FAQ'}
          </Typography>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Question"
              variant="outlined"
              value={editingFAQ?.question || ''}
              onChange={(e) => setEditingFAQ({ ...editingFAQ, question: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              fullWidth
              label="Answer"
              multiline
              rows={4}
              variant="outlined"
              value={editingFAQ?.answer || ''}
              onChange={(e) => setEditingFAQ({ ...editingFAQ, answer: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              fullWidth
              label="Status"
              select
              variant="outlined"
              value={editingFAQ?.status || 'Active'}
              onChange={(e) => setEditingFAQ({ ...editingFAQ, status: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} sx={{ color: '#666', textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#F25828',
              '&:hover': { bgcolor: '#d44a20' },
              borderRadius: '10px',
              textTransform: 'none',
              px: 4,
              fontWeight: 600,
            }}
          >
            {editingFAQ?.id ? 'Update FAQ' : 'Save FAQ'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete FAQ?"
        message={`Are you sure you want to delete this FAQ? This action cannot be undone.`}
      />
    </Box>
  );
};

export default AdminFAQs;
